/**
 * Numerical / Quantitative Reasoning Test (NRT) — simplified (10 items).
 * Question images: client/public/asset/images/ability-test/NRT/
 */
import { nrtQuestionImageUrl } from './abilityTestImages.js';

const L = (en, tc, sc) => ({
  en,
  tc: tc != null ? tc : en,
  sc: sc != null ? sc : en
});

function st5(correct) {
  return {
    A: correct === 'A' ? 1 : 0,
    B: correct === 'B' ? 1 : 0,
    C: correct === 'C' ? 1 : 0,
    D: correct === 'D' ? 1 : 0,
    E: correct === 'E' ? 1 : 0
  };
}

/**
 * @param {number} displayNumber
 * @param {string|{en:string,tc:string,sc:string}} prompt
 * @param {[string, (string|{en:string,tc:string,sc:string})][]} optionRows [key, label]
 * @param {'A'|'B'|'C'|'D'|'E'} correctKey
 */
function nrtQuestion(displayNumber, prompt, optionRows, correctKey) {
  return {
    id: `nrt-q${displayNumber}`,
    displayNumber,
    imageUrl: nrtQuestionImageUrl(displayNumber),
    prompt: typeof prompt === 'string' ? L(prompt) : prompt,
    options: optionRows.map(([key, label]) => ({
      key,
      label: typeof label === 'string' ? L(label) : label
    })),
    scoreTable: st5(correctKey)
  };
}

function section(id, titleEn, titleTc, titleSc, questions) {
  return {
    id,
    title: L(titleEn, titleTc, titleSc),
    passages: [],
    questions
  };
}

export const quantitativeReasoningTestPaper = {
  slug: 'quantitative-reasoning',
  title: L('Quantitative Reasoning', '數量推理', '数量推理'),
  paperInstruction: L(
    'Read each question and the accompanying chart or table in the image. Select the single best answer (A–E). You may use the on-screen calculator if needed.',
    '請閱讀每道題目及附圖中的圖表或數據，選出最佳答案（A–E）。如有需要可使用計算機。',
    '请阅读每道题目及附图中的图表或数据，选出最佳答案（A–E）。如有需要可使用计算器。'
  ),
  timeLimitSeconds: 3600,
  order: 4,
  isPublished: true,
  sections: [
    section(
      'nrt-entrepreneurs',
      'Entrepreneurial drive',
      '案例：創業精神',
      '案例：创业精神',
      [
      nrtQuestion(
        1,
        L(
          "What percentage of entrepreneurs said that 'Determination' is necessary?",
          "有多少百分比的創業者認為「決心」是必要的？",
          "有多少百分比的创业者认为“决心”是必要的？"
        ),
        [
          ['A', '34.70%'],
          ['B', '45.60%'],
          ['C', '23.70%'],
          ['D', '53.70%'],
          ['E', '49.10%']
        ],
        'A'
      ),
      nrtQuestion(
        2,
        L(
          "What is the ratio of the number of entrepreneurs who deem 'Start-up capital' as necessary to the number of entrepreneurs who deem 'Education' as necessary?",
          "認為「創業資金」必要的創業者人數，與認為「教育」必要的創業者人數之比是多少？",
          "认为“创业资金”必要的创业者人数，与认为“教育”必要的创业者人数之比是多少？"
        ),
        [
          ['A', '1:2'],
          ['B', '1:3'],
          ['C', '2:5'],
          ['D', '3:4'],
          ['E', '2:3']
        ],
        'C'
      )
    ]),
    section(
      'nrt-profit',
      'Targets and outcomes',
      '案例：目標與結果',
      '案例：目标与结果',
      [
      nrtQuestion(
        3,
        L(
          'In June 2008, how much lower (in percentage terms) was the actual profit in comparison to its revised profit target?',
          '在 2008 年 6 月，實際利潤比修訂後的利潤目標低多少（以百分比計）？',
          '在 2008 年 6 月，实际利润比修订后的利润目标低多少（以百分比计）？'
        ),
        [
          ['A', '54%'],
          ['B', '49%'],
          ['C', '63%'],
          ['D', '56%'],
          ['E', '46%']
        ],
        'A'
      ),
      nrtQuestion(
        4,
        L(
          'In April 2008, what was the ratio of the difference between the initial target profit and the revised target profit, to the actual profit made?',
          '在 2008 年 4 月，（最初目標利潤 − 修訂後目標利潤）與實際利潤之比是多少？',
          '在 2008 年 4 月，（最初目标利润 − 修订后目标利润）与实际利润之比是多少？'
        ),
        [
          ['A', '1:2'],
          ['B', '1:3'],
          ['C', '4:5'],
          ['D', '6:7'],
          ['E', '8:7']
        ],
        'D'
      )
    ]),
    section(
      'nrt-fashion',
      'Market signals',
      '案例：市場訊號',
      '案例：市场信号',
      [
      nrtQuestion(
        5,
        L(
          'Assuming the total revenue in Q2 2007 was £138.4 million, which division had the second lowest revenue across both Q1 2007 and Q2 2007?',
          '假設 2007 年第二季（Q2）總收入為 £138.4 百萬，2007 年第一季（Q1）及第二季（Q2）兩季合計下，哪個部門的收入是第二低？',
          '假设 2007 年第二季（Q2）总收入为 £138.4 百万，2007 年第一季（Q1）及第二季（Q2）两季合计下，哪个部门的收入是第二低？'
        ),
        [
          ['A', L('Female', '女裝', '女装')],
          ['B', L('Male', '男裝', '男装')],
          ['C', L('Kids', '童裝', '童装')],
          ['D', L('Accessories', '配飾', '配饰')],
          ['E', L('Shoes', '鞋履', '鞋类')]
        ],
        'E'
      ),
      nrtQuestion(
        6,
        L(
          'Assuming the trend for the increase in revenue for Female fashion from Q1 to Q2 continues to Q3, what would be the revenue for Female fashion in Q3 2007? Assume the total revenue for Q2 2007 was £138.4 million.',
          '假設女裝收入由 2007 年 Q1 至 Q2 的增長趨勢延續至 Q3，則 2007 年 Q3 的女裝收入會是多少？（假設 2007 年 Q2 總收入為 £138.4 百萬。）',
          '假设女装收入由 2007 年 Q1 至 Q2 的增长趋势延续至 Q3，则 2007 年 Q3 的女装收入会是多少？（假设 2007 年 Q2 总收入为 £138.4 百万。）'
        ),
        [
          ['A', '£83.4m'],
          ['B', '£91.2m'],
          ['C', '£66.8m'],
          ['D', '£75.2m'],
          ['E', '£81.1m']
        ],
        'A'
      )
    ]),
    section(
      'nrt-investment',
      'Resource choices',
      '案例：資源抉擇',
      '案例：资源抉择',
      [
      nrtQuestion(
        7,
        L(
          "What is the ratio of the average investment in Marketing over 2008 and 2009, to the total investment in 'Other' in both 2008 and 2009?",
          '2008 與 2009 年「市場推廣」投資的平均值，與 2008 與 2009 年「其他」投資總和之比是多少？',
          '2008 与 2009 年“市场推广”投资的平均值，与 2008 与 2009 年“其他”投资总和之比是多少？'
        ),
        [
          ['A', '3:7'],
          ['B', '11:15'],
          ['C', '9:25'],
          ['D', '4:13'],
          ['E', '11:17']
        ],
        'C'
      ),
      nrtQuestion(
        8,
        L(
          "What percentage of the total investment in 2009 is accounted for by Research & Development?",
          '2009 年的總投資中，「研發」所佔百分比是多少？',
          '2009 年的总投资中，“研发”所占百分比是多少？'
        ),
        [
          ['A', '12.90%'],
          ['B', '14.30%'],
          ['C', '15.60%'],
          ['D', '11.20%'],
          ['E', '13.90%']
        ],
        'B'
      )
    ]),
    section(
      'nrt-poll',
      'Public opinion',
      '案例：民意角度',
      '案例：民意角度',
      [
      nrtQuestion(
        9,
        L(
          'How many respondents took part in the poll?',
          '共有多少名受訪者參與了這次調查？',
          '共有多少名受访者参与了这次调查？'
        ),
        [
          ['A', L('1,745 respondents', '1,745 名受訪者', '1,745 名受访者')],
          ['B', L('1,895 respondents', '1,895 名受訪者', '1,895 名受访者')],
          ['C', L('2,763 respondents', '2,763 名受訪者', '2,763 名受访者')],
          ['D', L('5,685 respondents', '5,685 名受訪者', '5,685 名受访者')],
          ['E', L('9,475 respondents', '9,475 名受訪者', '9,475 名受访者')]
        ],
        'B'
      ),
      nrtQuestion(
        10,
        L(
          'How many responses in total were recorded by the poll?',
          '本次調查共記錄了多少個回應？',
          '本次调查共记录了多少个回应？'
        ),
        [
          ['A', L('1,745 responses', '1,745 個回應', '1,745 个回应')],
          ['B', L('1,895 responses', '1,895 個回應', '1,895 个回应')],
          ['C', L('2,763 responses', '2,763 個回應', '2,763 个回应')],
          ['D', L('5,685 responses', '5,685 個回應', '5,685 个回应')],
          ['E', L('9,475 responses', '9,475 個回應', '9,475 个回应')]
        ],
        'E'
      )
    ])
  ]
};

